import { Fragment, useCallback, useEffect, useMemo, useState, type ChangeEvent, type KeyboardEvent } from 'react';
import { useTheme } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import { Button } from '../../../../Controls/Button/index.tsx';
import { NothingFound } from '../../../../Elements/Images/index.ts';
import { Loader } from '../../../../Elements/Loader/index.tsx';
import { DEFAULT_DROPDOWN_WIDTH, UNGROUPED_KEY } from '../../constants.ts';
import { groupOptions, hasGroups, isEqualOptions } from '../../helpers.ts';
import type { DropdownProps, Option } from '../../types.ts';
import { Search, type SearchInputAutofocus, AUTOFOCUS_WITHOUT_SCROLL } from '../Search.tsx';
import { OptionsList } from './OptionsList.tsx';
import { DropdownBody, FooterWrapper, GroupHeaderWrapper, HeaderWrapper, OptionsWrapper, DropdownWrapper } from './styled.ts';

export const Dropdown = <T,>({
  onChange,
  selected,
  isMulti,
  loading,
  isClearable = true,
  getOptionKey,
  serverSideFiltering,
  filter,
  setFilter,
  filteredOptions,
  total,
  noMatchesText = 'No matches found',
  clearSelectionText = 'Clear selection',
  getFilteredText = (filtered, totalItems) => `${filtered} of ${totalItems}`,
  getSelectedText = (totalSelected) => `${totalSelected} selected`,
  getTotalText = (totalItems) => `${totalItems} total`,
  optionSelected,
  close,
  formatOptionLabel,
  autofocus = AUTOFOCUS_WITHOUT_SCROLL,
  dropdownWidth = DEFAULT_DROPDOWN_WIDTH,
}: DropdownProps<T> & {
  filter: string;
  setFilter: (value: string) => void;
  filteredOptions: Option<T>[];
  total: number;
  close: () => void;
  isClearable?: boolean;
  autofocus?: SearchInputAutofocus;
}) => {
  const theme = useTheme();

  // Keyboard navigation state
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);

  // eslint-disable-next-line no-restricted-syntax
  const handleClick = useCallback(
    (option: Option<T>) => () => {
      if (isMulti) {
        const copy = [...(selected ?? [])];
        const index = copy.findIndex((item) => (optionSelected ? optionSelected(item, option) : isEqualOptions(item, option)));
        if (index !== -1) {
          if (isClearable || copy.length > 1) {
            copy.splice(index, 1);
          }
        } else {
          copy.push(option);
        }
        onChange(copy);
      } else if (isClearable) {
        if (optionSelected) {
          onChange(selected && optionSelected(selected, option) ? null : option);
        } else {
          onChange(selected && isEqualOptions(selected, option) ? null : option);
        }
        close();
      } else {
        onChange(option);
        close();
      }
    },
    [close, isClearable, isMulti, onChange, optionSelected, selected],
  );

  const handleClear = useCallbackRef(() => {
    if (!isClearable) {
      return;
    }

    if (isMulti) {
      onChange([]);
    } else {
      onChange(null);
    }
  });

  const handleSearchChange = useCallbackRef((e: ChangeEvent<HTMLInputElement>) => {
    setFilter(e.target.value);
  });

  const handleSearchClear = useCallbackRef(() => {
    setFilter('');
  });

  // Keyboard navigation handler
  const handleKeyDown = useCallbackRef((e: KeyboardEvent) => {
    const optionsToNavigate = filteredOptions;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((prev) => (prev < optionsToNavigate.length - 1 ? prev + 1 : 0));
        break;

      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((prev) => (prev > 0 ? prev - 1 : optionsToNavigate.length - 1));
        break;

      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < optionsToNavigate.length) {
          const option = optionsToNavigate[focusedIndex];
          if (option) {
            handleClick(option)();
          }
        }
        break;

      case 'Escape':
        e.preventDefault();
        close();
        break;

      case 'Tab':
        // Allow default Tab behavior but close the dropdown
        close();
        break;

      default:
        break;
    }
  });

  // Reset focused index when user types in the search
  useEffect(() => {
    if (filter) {
      setFocusedIndex(filteredOptions.length > 0 ? 0 : -1);
    } else {
      setFocusedIndex(-1);
    }
  }, [filter, filteredOptions.length]);

  const hasValue = useMemo(() => Boolean(isMulti ? selected.length : selected), [isMulti, selected]);

  const selectedCount = useMemo(() => (Array.isArray(selected) ? selected.length : 1), [selected]);

  const stateDescription = useMemo(() => {
    if (filteredOptions.length === total && !hasValue) {
      return getTotalText(total);
    }

    if (hasValue) {
      return getSelectedText(selectedCount);
    }

    return getFilteredText(filteredOptions.length, total);
  }, [filteredOptions.length, getFilteredText, getSelectedText, getTotalText, hasValue, selectedCount, total]);

  const withGroups = useMemo(() => hasGroups(filteredOptions), [filteredOptions]);

  const groupedOptions = useMemo(() => (withGroups ? groupOptions(filteredOptions) : null), [withGroups, filteredOptions]);

  return (
    <DropdownWrapper onKeyDown={handleKeyDown} tabIndex={-1} role='listbox' aria-multiselectable={isMulti}>
      {(!!serverSideFiltering || (!loading && total > 6)) && (
        <HeaderWrapper>
          <Search value={filter} onChange={handleSearchChange} onClear={handleSearchClear} autofocus={autofocus} />
        </HeaderWrapper>
      )}
      {!loading && (
        <>
          {filteredOptions.length === 0 && (
            <DropdownBody data-testid='no-matches'>
              <NothingFound size={90} />
              {noMatchesText}
            </DropdownBody>
          )}
          {filteredOptions.length > 0 && (
            <>
              <OptionsWrapper>
                {groupedOptions ? (
                  groupedOptions.map((group, groupIndex) => {
                    // Calculate the starting index for this group
                    const startIndex = groupedOptions.slice(0, groupIndex).reduce((acc, g) => acc + g.options.length, 0);
                    return (
                      <Fragment key={group.groupName}>
                        {group.groupName !== UNGROUPED_KEY && <GroupHeaderWrapper>{group.groupName}</GroupHeaderWrapper>}
                        <OptionsList
                          options={group.options}
                          groupName={group.groupName}
                          isMulti={isMulti}
                          selected={selected}
                          focusedIndex={focusedIndex - startIndex}
                          getOptionKey={getOptionKey}
                          optionSelected={optionSelected}
                          formatOptionLabel={formatOptionLabel}
                          handleClick={handleClick}
                        />
                      </Fragment>
                    );
                  })
                ) : (
                  <OptionsList
                    options={filteredOptions}
                    isMulti={isMulti}
                    selected={selected}
                    focusedIndex={focusedIndex}
                    getOptionKey={getOptionKey}
                    optionSelected={optionSelected}
                    formatOptionLabel={formatOptionLabel}
                    handleClick={handleClick}
                  />
                )}
              </OptionsWrapper>
              <FooterWrapper width={dropdownWidth} data-testid='filter-footer'>
                <span>
                  {isClearable && hasValue && (
                    <Button ghost dataTestId='clear-selection' onClick={handleClear}>
                      {clearSelectionText}
                    </Button>
                  )}
                </span>
                <span data-testid='footer-selected-amount'>{stateDescription}</span>
              </FooterWrapper>
            </>
          )}
        </>
      )}
      {loading && (
        <DropdownBody>
          <Loader fill={theme.colors.grey100_1} />
        </DropdownBody>
      )}
    </DropdownWrapper>
  );
};

export const DropdownWithFilterState = <T,>(props: DropdownProps<T> & { close: () => void }) => {
  const { options, formatOptionLabel } = props;
  const [filter, setFilter] = useState<string>('');

  const filteredOptions = useMemo<Option<T>[]>(
    () =>
      options
        .filter((option) => {
          if (filter.length === 0) return true;

          const lowerFilter = filter.toLowerCase();
          const formattedLabel = formatOptionLabel?.(option);

          const label = typeof formattedLabel === 'string' ? formattedLabel : option.label;
          const matchesLabel = label.toLowerCase().includes(lowerFilter);
          const matchesGroup = option.group?.toLowerCase().includes(lowerFilter) || false;

          return matchesLabel || matchesGroup;
        })
        .slice(0, 100),
    [options, filter, formatOptionLabel],
  );

  const totalCount = useMemo(() => options.length, [options.length]);

  const dropdownProps = useMemo(
    () => ({
      ...props,
      filter,
      setFilter,
      filteredOptions,
      total: totalCount,
    }),
    [props, filter, setFilter, filteredOptions, totalCount],
  );

  return <Dropdown {...dropdownProps} />;
};
